import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();

  const services = [
    { icon: 'ri-window-line', key: 'windowScreens' },
    { icon: 'ri-door-open-line', key: 'slidingDoors' },
    { icon: 'ri-collage-line', key: 'retractable' },
    { icon: 'ri-home-4-line', key: 'petDoors' },
    { icon: 'ri-sun-line', key: 'solarScreens' },
    { icon: 'ri-tools-line', key: 'repair' },
    { icon: 'ri-layout-grid-line', key: 'custom' },
  ];

  return (
    <section className="relative w-full">
      {/* Hero area with background image */}
      <div className="relative w-full overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            alt="The Screen Squad - Instalación de mosquiteros para ventanas y puertas"
            className="absolute inset-0 w-full h-full object-cover object-center"
            src="https://static.readdy.ai/image/e2264d4847cbc98dfbe6fd1d0e6b7ca4/d3579cf4890bf3e58f8b4a5b869588a3.jpeg"
          />
          {/* Dark navy gradient overlay from left */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#081326] from-0% via-[#0a1628]/90 via-50% to-transparent to-100%" />
        </div>

        {/* Main content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-32 pb-5">
          {/* Top 3 trust badges */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-8">
            <div className="flex items-center gap-2 text-white text-xs md:text-sm font-bold uppercase tracking-wide">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shield-check-line text-accent-500 text-lg" />
              </span>
              <span className="leading-tight">{t('hero.topBadges.professional')}</span>
            </div>
            <div className="flex items-center gap-2 text-white text-xs md:text-sm font-bold uppercase tracking-wide">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-truck-line text-accent-500 text-lg" />
              </span>
              <span className="leading-tight">{t('hero.topBadges.mobile')}</span>
            </div>
            <div className="flex items-center gap-2 text-white text-xs md:text-sm font-bold uppercase tracking-wide">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-time-line text-accent-500 text-lg" />
              </span>
              <span className="leading-tight">{t('hero.topBadges.fast')}</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-[3.75rem] font-extrabold text-white leading-[1.1] mb-4 max-w-3xl">
            {t('hero.titleLine1')}{' '}
            <span className="text-accent-500">{t('hero.titleHighlight')}</span>{' '}
            {t('hero.titleLine2')}
          </h1>

          {/* Blue accent line */}
          <div className="w-24 h-1 bg-primary-500 mb-6" />

          {/* Description */}
          <p className="text-white/90 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
            {t('hero.descriptionPart1')}
            <span className="text-accent-500 font-semibold">{t('hero.descriptionHighlight1')}</span>
            {t('hero.descriptionPart2')}
            <span className="text-accent-500 font-semibold">{t('hero.descriptionHighlight2')}</span>
            {t('hero.descriptionPart3')}
          </p>

          {/* 4 feature badges */}
          <div className="flex flex-wrap gap-3 mb-2">
            <div className="flex items-center gap-2.5 bg-white rounded-lg px-4 py-2.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-time-line text-primary-500 text-base" />
              </span>
              <div>
                <p className="text-gray-900 text-[11px] font-bold uppercase tracking-wide leading-tight">{t('hero.badges.fast')}</p>
                <p className="text-gray-500 text-[10px] leading-tight">{t('hero.badges.fastSub')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 bg-white rounded-lg px-4 py-2.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-line text-primary-500 text-base" />
              </span>
              <p className="text-gray-900 text-[11px] font-bold uppercase tracking-wide leading-tight max-w-[120px]">{t('hero.badges.materials')}</p>
            </div>

            <div className="flex items-center gap-2.5 bg-white rounded-lg px-4 py-2.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-primary-500 text-base" />
              </span>
              <p className="text-gray-900 text-[11px] font-bold uppercase tracking-wide leading-tight max-w-[120px]">{t('hero.badges.quotes')}</p>
            </div>

            <div className="flex items-center gap-2.5 bg-white rounded-lg px-4 py-2.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-award-line text-primary-500 text-base" />
              </span>
              <p className="text-gray-900 text-[11px] font-bold uppercase tracking-wide leading-tight max-w-[120px]">{t('hero.badges.guarantee')}</p>
            </div>
          </div>

          {/* CTA Buttons + Google Badge */}
          <div className="flex flex-wrap items-end gap-4">
            <a
              href="tel:+16574338533"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-accent-500 hover:bg-accent-600 text-[#1a1a1a] font-bold px-7 py-5 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-phone-fill text-xl" />
              </span>
              <div className="leading-tight text-left">
                <p className="text-base font-extrabold">{t('hero.cta.call')}</p>
                <p className="text-sm font-bold opacity-90">(657) 433-8533</p>
              </div>
            </a>

            <a
              href="https://wa.me/16574338533"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1faa52] text-white font-bold px-7 py-5 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl" />
              </span>
              <div className="leading-tight text-left">
                <p className="text-base font-extrabold">{t('hero.cta.whatsapp')}</p>
                <p className="text-sm font-normal opacity-95">{t('hero.cta.whatsappSub')}</p>
              </div>
            </a>

            {/* Google Reviews Badge */}
            <div className="hidden md:flex items-start gap-3 bg-black/45 backdrop-blur-sm rounded-lg px-5 py-4 ml-auto">
              <span className="w-11 h-11 flex items-center justify-center rounded-full bg-white shrink-0">
                <i className="ri-google-fill text-[#4285F4] text-2xl" />
              </span>
              <div>
                <div className="flex gap-0.5 mb-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm" />
                    </span>
                  ))}
                </div>
                <p className="text-white text-sm font-bold leading-tight">{t('hero.google.rating')}</p>
                <p className="text-white/85 text-xs leading-tight mb-0.5">{t('hero.google.reviews')}</p>
                <a
                  href="https://g.co/kgs/example"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-500 hover:text-accent-400 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer"
                >
                  {t('hero.google.viewReviews')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom blue bar - INSIDE the hero, no gap */}
        <div className="relative z-10 w-full bg-[#0a2a5c]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Services */}
              <div className="flex-1">
                <h3 className="text-white text-xs font-bold uppercase tracking-wider mb-3">{t('hero.bottomBar.servicesTitle')}</h3>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-x-2 gap-y-3">
                  {services.map((s) => (
                    <div key={s.key} className="flex flex-col items-center gap-1 text-center">
                      <span className="w-7 h-7 flex items-center justify-center">
                        <i className={`${s.icon} text-white text-lg`} />
                      </span>
                      <p className="text-white/80 text-[9px] font-semibold leading-tight uppercase tracking-wide">
                        {t(`hero.bottomBar.services.${s.key}`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="hidden lg:block w-px bg-white/15 self-stretch" />

              {/* Areas */}
              <div className="lg:w-[420px] shrink-0">
                <h3 className="text-accent-500 text-xs font-bold uppercase tracking-wider mb-3">{t('hero.bottomBar.areasTitle')}</h3>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  {[
                    'santaAna', 'anaheim', 'huntingtonBeach',
                    'tustin', 'irvine', 'newportBeach',
                    'orange', 'costaMesa', 'more',
                    'gardenGrove', 'fountainValley',
                  ].map((area) => (
                    <div key={area} className="flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-checkbox-circle-fill text-accent-500 text-sm" />
                      </span>
                      <span className="text-white text-xs font-medium whitespace-nowrap">{t(`hero.bottomBar.areas.${area}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}